import { DtoCubeDataResponse } from "@/types/dto-types";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  getCountsByQuadrant,
  QuadrantScatter,
} from "../../../../ParticipationFlow/components/quadrant";
import { CATEGORY_ICONS, ChallengeRow } from "../../../components/ChallengeRow";
import { CockpitSidePanel } from "../../../components/CockpitSidePanel";
import { DoubleToggle } from "../../../components/DoubleToggle";
import { ScoreBenchmarkOverview } from "../../../components/ScoreBenchmarkOverview";
import { Cube } from "../../../components/Сube";
import {
  CUBE_SIDE_NAME_TO_NUMBER,
  CUBE_SIDE_NUMBER_TO_NAME,
} from "../../../components/Сube/constants";
import { CubeSideName } from "../../../components/Сube/types";
import { SelectedFilters, useHomePageData, useUrlState } from "../../../hooks";
import { Benchmark } from "../../../hooks/useBenchmarks";
import { useIsMobile } from "../../../hooks/useIsMobile";
import { CalculationType, CategoryNavItem } from "../../../types";
import { renderNumber } from "../../../utils";
import { ViewMode } from "./../types";
import "./style.scss";
import { getTranslations } from "./translations";

export interface HomePageProps {
  benchmarks: Benchmark[];
  selectedBenchmark?: Benchmark;
  setSelectedBenchmark: (benchmark: Benchmark) => void;
  rawData?: DtoCubeDataResponse;
  currentTestId: string;
  selectedFilters: SelectedFilters | undefined;
  selectedCalculationType: CalculationType;
  setSelectedCalculationType: (calculationType: CalculationType) => void;
}
export function HomePage({
  benchmarks,
  selectedBenchmark,
  setSelectedBenchmark,
  selectedFilters,
  currentTestId,
  rawData,
  selectedCalculationType,
  setSelectedCalculationType,
}: HomePageProps) {
  const { t } = useTranslation();
  const { updatePath } = useUrlState();

  const [hoveredCubeSide, setHoveredCubeSide] = useState<CubeSideName>();
  const [viewMode, setViewMode] = useState<ViewMode>(ViewMode.Cube);
  const [hoveredChallengeIndex, setHoveredChallengeIndex] = useState<number>();

  const homePageData = useHomePageData({
    rawData,
    currentTestId,
    selectedFilters,
    benchmarkTitle:
      selectedBenchmark?.id === "default"
        ? undefined
        : selectedBenchmark?.title,
  });
  const isMobile = useIsMobile();

  const cubeData = Object.entries(homePageData?.categoryScores ?? {}).reduce(
    (acc, [key, value]) => ({
      ...acc,
      [CUBE_SIDE_NAME_TO_NUMBER[key]]:
        value[selectedCalculationType].cubeSideFillArea,
    }),
    {} as Record<CubeSideName, number | undefined>
  );

  const translations = getTranslations(t);

  function navigateToCategory(category: CategoryNavItem) {
    updatePath({ category });
  }

  const renderCubeInfo = (category: CategoryNavItem) => {
    const categoryData = homePageData.categoryScores[category];
    const value = categoryData[selectedCalculationType].percentageValue;
    const isActive = CUBE_SIDE_NUMBER_TO_NAME[hoveredCubeSide!] === category;
    const title = translations.categories[category]!.title;

    return (
      <div
        className={`cube-info ${isActive ? "active" : ""}`}
        onClick={() => navigateToCategory(category)}
        style={{ cursor: "pointer" }}
      >
        <div className="cube-inner">
          <div className="cube-title">
            <div className="cube-icon">{(CATEGORY_ICONS as any)[category]}</div>
            <div>{title}</div>
          </div>
          <div className="cube-value">
            {renderNumber(
              value,
              selectedCalculationType === CalculationType.Benchmark
            )}
            %
          </div>
        </div>
      </div>
    );
  };

  const renderChallengeSidePanel = (hoveredChallengeIndex: number) => {
    const data = homePageData.challenges[hoveredChallengeIndex];

    return (
      <CockpitSidePanel
        title={data.info.name}
        score={data.score}
        benchmark={data.benchmark}
        difference={data.scoreVsBenchmarkDifference}
        description={data.info.infoChallenge}
      />
    );
  };

  const renderCubeSidePanel = (hoveredCubeSide: CubeSideName) => {
    const category = CUBE_SIDE_NUMBER_TO_NAME[
      hoveredCubeSide
    ] as CategoryNavItem;
    const data = homePageData.categoryScores[category];
    const categoryTexts = translations.categories[category]!;

    return (
      <CockpitSidePanel
        title={categoryTexts.hoverTitle}
        score={data.averageScore}
        benchmark={data.averageBenchmark}
        difference={data.scoreVsBenchmarkDifference}
        description={categoryTexts.description}
      />
    );
  };

  return (
    <div className="analytics-home-page">
      <div className="left">
        <ScoreBenchmarkOverview
          isCategoryPage={false}
          score={homePageData.score}
          selectedBenchmark={selectedBenchmark}
          benchmarks={benchmarks}
          setSelectedBenchmark={setSelectedBenchmark}
        />
        <section className="cube-overview">
          <section className="cube-toggle">
            <DoubleToggle
              selectedOptionId={viewMode}
              leftOption={{
                id: ViewMode.Cube,
                label: t("frontend.futurebilityCockpit.cube", "Cube"),
              }}
              rightOption={{
                id: ViewMode.Challenges,
                label: t(
                  "frontend.futurebilityCockpit.challenges",
                  "Challenges"
                ),
              }}
              onChange={optionId => {
                setViewMode(optionId as ViewMode);
              }}
            />
          </section>
          <section className="cube-section">
            {viewMode === "cube" && (
              <div className="cube-wrap">
                <div className="cube-top">{renderCubeInfo("organization")}</div>
                <div className="cube-center">
                  <Cube
                    greyOutOtherSidesOnHover
                    mode="collapsed"
                    values={cubeData}
                    onSideHover={side => {
                      setHoveredCubeSide(side);
                    }}
                    onSideClick={side => {
                      updatePath({
                        category: CUBE_SIDE_NUMBER_TO_NAME[
                          side
                        ] as CategoryNavItem,
                      });
                    }}
                  />
                  {hoveredCubeSide && renderCubeSidePanel(hoveredCubeSide)}
                </div>
                <div className="cube-bottom">
                  {renderCubeInfo("team")}
                  {renderCubeInfo("leadership")}
                </div>
              </div>
            )}
            {viewMode === "challenges" && (
              <div>
                {homePageData.challenges.map((challenge, index) => (
                  <ChallengeRow
                    key={index}
                    id={index}
                    title={challenge.info.name}
                    percentage={renderNumber(
                      challenge.scoreVsBenchmarkDifference,
                      true
                    )}
                    type={challenge.category as CategoryNavItem}
                    onHover={setHoveredChallengeIndex}
                  />
                ))}
                {hoveredChallengeIndex !== undefined &&
                  renderChallengeSidePanel(hoveredChallengeIndex)}
              </div>
            )}
          </section>
          {viewMode === ViewMode.Cube && (
            <section className="cube-toggle">
              <DoubleToggle
                leftOption={{
                  id: CalculationType.Benchmark,
                  label: t(
                    "frontend.futurebilityCockpit.scoreType.benchmark",
                    "Benchmark"
                  ),
                }}
                rightOption={{
                  id: CalculationType.AverageScore,
                  label: t(
                    "frontend.futurebilityCockpit.scoreType.corporateScore",
                    "Corporate Score"
                  ),
                }}
                selectedOptionId={selectedCalculationType}
                onChange={optionId => {
                  setSelectedCalculationType(optionId as CalculationType);
                }}
              />
            </section>
          )}
        </section>
      </div>
      <div className="right">
        <div className="quadrant-wrap-content">
          <div className="quadrant-side-title">
            <div className="title-left">
              {t("frontend.futurebilityCockpit.marketType", "Market-Type")}
            </div>
            <div className="title-right">
              {
                translations.quadrantTopHeadlines[
                  getCountsByQuadrant(homePageData.marketItems).indexOf(
                    Math.max(...getCountsByQuadrant(homePageData.marketItems))
                  )
                ]
              }
            </div>
          </div>
          <QuadrantScatter
            {...translations.chartTitles}
            quadrantSidePanelTexts={translations.quadrantSidePanelTexts}
            quadrantSidePanelTitles={translations.quadrantSidePanelTitles}
            quadrantTitles={translations.quadrantTitles}
            points={homePageData.marketItems}
            width={isMobile ? 325 : 480}
            height={isMobile ? 325 : 480}
          />
        </div>
      </div>
    </div>
  );
}
